import React, {useEffect, useState, useRef} from "react";
import { FiFolder, FiChevronDown, FiChevronRight, FiStar } from "react-icons/fi";
import ChatListItem from "./ChatListItem/ChatListItem.tsx";
import {useAppStore} from "../../../GlobalStore/GlobalStore.ts";
import { isChrome, isIde } from "../../../Utils/misc/envNameUtils.ts";

interface ChatListProps {}

const ChatList: React.FC<ChatListProps> = ({ }) => {
    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const { isIncognito } = settingsObj;
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const { chatHistory } = dynamicStateObj;
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const { environment } = environmentObj;
    const userObj = useAppStore((state: any) => state.userObj);
    const { user } = userObj;

    const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);
    const [editingChatId, setEditingChatId] = useState<string | null>(null);
    const [isFavoritesExpanded, setIsFavoritesExpanded] = useState<boolean>(false);
    const dropdownRef = useRef<any>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setActiveDropdownId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownRef]);

    const handleDropdownToggle = (chatId: string) => {
        setActiveDropdownId((prevId) => {
            if (editingChatId !== null) {
                setDynamicStateObj({editingChatId: null});
            }
            return prevId === chatId ? null : chatId;
        });
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const month = date.toLocaleString('default', { month: 'long', timeZone: 'UTC' });
        const day = date.getUTCDate();
        const year = date.getUTCFullYear();
        return `${month} ${day}, ${year}`;
    };

    // Separate favorites from regular chats
    const favoriteChats = chatHistory.filter((chat: any) => chat.isFavorite);
    const regularChats = chatHistory.filter((chat: any) => !chat.isFavorite);

    const groupedChats = regularChats.reduce((groups: any[], chat: any) => {
        let { lastAccessedDate } = chat;
        lastAccessedDate = formatDate(lastAccessedDate);

        if (!groups[lastAccessedDate]) {
            groups[lastAccessedDate] = [];
        }
        groups[lastAccessedDate].push(chat);
        return groups;
    }, {} as { [key: string]: any[] });

    // Determine if arrow should be at the end (web/mobile) or beginning (chrome/ide)
    const shouldShowArrowAtEnd = !isChrome(environment) && !isIde(environment);

    return (
        <div className={`flex-grow overflow-hidden flex flex-col ${isIncognito ? 'opacity-30 pointer-events-none' : ''}`}>
            <ul className="overflow-y-auto border-t border-neutral-800 flex-grow my-2 text-sm text-neutral-900 dark:text-neutral-200">
                {/* Render Favorites folder only when user is signed in */}
                {user && (
                    <li key="favorites">
                        <div className="pb-1 pt-5">
                            <button
                                onClick={() => setIsFavoritesExpanded(!isFavoritesExpanded)}
                                className="w-full text-left px-3 py-3 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded flex items-center gap-2 text-neutral-900 dark:text-neutral-200 text-base"
                            >
                                {/* Arrow at beginning for Chrome and IDE */}
                                {!shouldShowArrowAtEnd && (
                                    isFavoritesExpanded ? <FiChevronDown className="h-4 w-4 transition-transform duration-150" /> : <FiChevronRight className="h-4 w-4 transition-transform duration-150" />
                                )}
                                <FiFolder className="h-4 w-4" />
                                <span className={"font-medium"}>Favorites</span>
                                <FiStar className="h-4 w-4 text-yellow-500" />
                                <span className="text-neutral-400 dark:text-neutral-500">({favoriteChats.length})</span>
                                {/* Arrow at end for Web and Mobile */}
                                {shouldShowArrowAtEnd && (
                                    isFavoritesExpanded ? <FiChevronDown className="h-4 w-4 transition-transform duration-150" /> : <FiChevronRight className="h-4 w-4 transition-transform duration-150" />
                                )}
                            </button>
                        </div>
                        <div className={`transition-all duration-400 ease-in-out ${isFavoritesExpanded ? 'max-h-96 overflow-visible' : 'max-h-0 overflow-hidden'}`}>
                            <ul className="ml-4">
                                {favoriteChats.map((chat: any) => (
                                    <ChatListItem
                                        key={chat.id}
                                        chat={chat}
                                        isDropdownOpen={activeDropdownId === chat.id}
                                        onDropdownToggle={handleDropdownToggle}
                                        isEditing={editingChatId === chat.id}
                                        ref={(node) => {
                                            if (chat.id === activeDropdownId) {
                                                dropdownRef.current = node;
                                            }
                                        }}
                                        setEditingChatId={setEditingChatId}
                                        setActiveDropdownId={setActiveDropdownId}
                                    />
                                ))}
                            </ul>
                        </div>
                    </li>
                )}

                {/* Separator line between favorites and regular chats - only show when user is signed in */}
                {user && (
                    <li>
                        <div className="px-3 pt-1">
                            <div className="border-t border-neutral-300 dark:border-neutral-700"></div>
                        </div>
                    </li>
                )}

                {/* Render regular chats grouped by date */}
                {Object.entries(groupedChats).map(([date, chats]) => (
                    <li key={date}>
                        <div className="pb-3 pt-5 text-center">
                            <span className={"text-neutral-800 dark:text-neutral-400 py-2 px-3 rounded"}>{date}</span>
                        </div>
                        <ul>
                            {(chats as any[]).map((chat: any) => (
                                <ChatListItem
                                    key={chat.id}
                                    chat={chat}
                                    isDropdownOpen={activeDropdownId === chat.id}
                                    onDropdownToggle={handleDropdownToggle}
                                    isEditing={editingChatId === chat.id}
                                    ref={(node) => {
                                        if (chat.id === activeDropdownId) {
                                            dropdownRef.current = node;
                                        }
                                    }}
                                    setEditingChatId={setEditingChatId}
                                    setActiveDropdownId={setActiveDropdownId}
                                />
                            ))}
                        </ul>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default ChatList;
